import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, TextField, MenuItem,
  Button, Stack, Chip, TablePagination, TableSortLabel, Avatar,
  Tooltip, InputAdornment, IconButton, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
} from '@mui/material';
import { Link } from 'react-router-dom';
import apiService from '../../services/apiServices';
import warningsLocal from '../../services/warningsLocalService';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import normalizeWorker from '../../services/workerNormalizer';
import usePermissions from '../../hooks/usePermissions';
import useConfirm from '../../hooks/useConfirm';
import Toast from '../actions/Toasts';
import { ReportPage, ExportMenu } from '../report/ReportKit';
import { downloadWarningPdf, getWarningPdfBase64, getWarningPdfFilename } from '../../services/warningPdfService';
import {
  SEVERITY_LEVELS, SEVERITY_RANK, severityMeta, canonicalSeverity, inRange, matchesQuery,
} from '../../config/warnings';
import {
  warningsLayout, exportWarningsExcel, downloadWarningsPdf, downloadWarningsCsv,
  printWarnings, dateText,
} from '../../utils/warningsExport';

function initials(first = '', last = '') {
  return `${first[0] || ''}${last[0] || ''}`.toUpperCase() || '?';
}

// ─── Normalizimi i një rreshti ────────────────────────────────────────────────
function normalizeWarning(r) {
  return {
    ...r,
    Employee:    r.Employee    ?? r.WorkerId ?? r.employeeId,
    Subject:     r.Subject     ?? r.Type     ?? '—',
    Severity:    canonicalSeverity(r.Severity ?? r.Type),
    Description: r.Description ?? r.Note     ?? '',
    CreatedAt:   r.CreatedAt   ?? r.Date     ?? '',
  };
}

function toSingleWarningPdf(r) { downloadWarningPdf(r); }

// ═══════════════════════════════════════════════════════════════════════════════
export default function WarningsReport() {
  const [rows,       setRows]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [loadError,  setLoadError]  = useState('');
  const [company,    setCompany]    = useState({});
  const [localRows,  setLocalRows]  = useState([]);
  const [sentRows,   setSentRows]   = useState([]);
  const [employees,  setEmployees]  = useState([]);
  const [departments,setDepartments]= useState([]);

  // Filters
  const [search, setSearch]   = useState('');
  const [emp,    setEmp]      = useState('all');
  const [dept,   setDept]     = useState('all');
  const [level,  setLevel]    = useState('all');
  const [from,   setFrom]     = useState('');
  const [to,     setTo]       = useState('');

  // Sorting & pagination
  const [orderBy, setOrderBy] = useState('CreatedAt');
  const [order,   setOrder]   = useState('desc');
  const [page,    setPage]    = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Row actions
  const [sendingId,  setSendingId]  = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [previewRow, setPreviewRow] = useState(null);
  const [snack,      setSnack]      = useState({ open: false, msg: '', sev: 'success' });

  // Fshirja e një vërejtjeje është veprim administrativ: `warnings.delete` e
  // mban vetëm admini (super_admin / company_admin), ndaj HR-ja që e shkruan
  // vërejtjen nuk e heq dot më vonë. Butoni as nuk shfaqet për të tjerët.
  const { can } = usePermissions();
  const canDelete = can('warnings.delete');
  const [confirm, confirmUI] = useConfirm();

  // ── Load data ──────────────────────────────────────────────────────────────
  // Më parë secila thirrje ishte një `.then()` pa `.catch()`: nëse serveri binte,
  // tabela shfaqte "Nuk u gjetën vërejtje" — pra një bazë e zbrazët dhe një
  // server i vdekur dukeshin njësoj.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [wRes, dRes, warnRes, setRes] = await Promise.all([
          apiService().get('workers'),
          apiService().get('departments'),
          apiService().get('employeeWarnings'),
          apiService().get('settings'),
        ]);
        if (!alive) return;
        const arr = (r) => { const raw = r?.data || r; return Array.isArray(raw) ? raw : []; };
        setEmployees(arr(wRes).map((w, i) => normalizeWorker(w, i)));
        setDepartments(arr(dRes));
        setRows(arr(warnRes).map(normalizeWarning));
        setCompany(arr(setRes)[0] || {});
      } catch (e) {
        if (alive) setLoadError('Të dhënat nuk u ngarkuan. Kontrollo lidhjen me serverin.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    // Rreshtat lokalë janë rezervë kur serveri s'i ka pranuar ende — lexohen
    // veçmas që një dështim i tyre të mos e rrëzojë ngarkimin kryesor.
    try { setLocalRows(warningsLocal().list().filter(it => !it._sent).map(normalizeWarning)); } catch (_) {}
    try { setSentRows(warningsLocal().listSent().map(normalizeWarning)); } catch (_) {}
    return () => { alive = false; };
  }, []);

  // reset pagination on filter change
  useEffect(() => { setPage(0); }, [search, emp, dept, level, from, to]);

  // ── Merge + enrich + filter ─────────────────────────────────────────────────
  const data = useMemo(() => {
    const deptMap = Object.fromEntries(departments.map(d => [String(d.Id), d.Name]));

    /**
     * Një vërejtje e vetme nuk duhet të dalë dy herë.
     *
     * Kopja lokale mbahej me një ID të sajuar (`sent-<koha>`), sepse ruajtja
     * s'e kthente ID-në e serverit. Krahasimi bëhej vetëm me ID, ndaj kopja
     * s'përputhej kurrë me rreshtin e bazës dhe e njëjta vërejtje shfaqej dy
     * herë — një krijim, dy rreshta. Tani ID-ja kthehet, por rreshtat e vjetër
     * te localStorage e kanë ende atë të sajuarën; prandaj krahasohet edhe
     * përmbajtja: i njëjti punëtor, i njëjti subjekt, i njëjti çast.
     */
    const signature = (r) => [
      String(r.Employee ?? ''),
      String(r.Subject ?? '').trim(),
      String(r.CreatedAt ?? '').slice(0, 19),
    ].join('|');

    const serverIds  = new Set(rows.map(r => String(r.Id ?? '')).filter(Boolean));
    const serverSigs = new Set(rows.map(signature));
    const isDuplicate = (r) => serverIds.has(String(r.Id ?? '')) || serverSigs.has(signature(r));

    const uniqueLocal = localRows.filter(r => !isDuplicate(r));
    const uniqueSent  = sentRows.filter(r => !isDuplicate(r));
    const merged = [...rows, ...uniqueLocal, ...uniqueSent].map(r => {
      const e = employees.find(x => String(x.Id) === String(r.Employee));
      const deptName = deptMap[String(e?.Department)] || e?.DepartmentName || '—';
      return {
        ...r,
        FirstName:      e?.FirstName  || '',
        LastName:       e?.LastName   || '',
        DepartmentName: deptName,
        Position:       e?.Position   || '',
        Email:          e?.Email      || '',
        // Fotoja rri te `Image` (Directory-a e ruan aty). Pa e marrë këtu,
        // punëtorët me foto dilnin gjithsesi si inicialet e tyre.
        PhotoSrc:       e?.Image      || e?.image || '',
      };
    });

    return merged.filter(r => {
      // Kërkimi dhe periudha janë rregulla të provueshme te `config/warnings`,
      // jo kushte të fshehura brenda një `useMemo` që s'e prek dot asnjë test.
      if (!matchesQuery(r, search))                              return false;
      if (emp   !== 'all' && String(r.Employee) !== String(emp)) return false;
      if (dept  !== 'all' && r.DepartmentName !== dept)          return false;
      if (level !== 'all' && r.Severity !== level)               return false;
      if (!inRange(r.CreatedAt, from, to))                       return false;
      return true;
    });
  }, [rows, localRows, sentRows, employees, departments, search, emp, dept, level, from, to]);

  // ── Sort ───────────────────────────────────────────────────────────────────
  const sorted = useMemo(() => {
    return [...data].sort((a, b) => {
      const dir = order === 'asc' ? 1 : -1;
      if (orderBy === 'Name') {
        const an = `${a.FirstName} ${a.LastName}`;
        const bn = `${b.FirstName} ${b.LastName}`;
        return dir * an.localeCompare(bn, 'sq');
      }
      if (orderBy === 'CreatedAt') {
        return dir * String(a.CreatedAt || '').localeCompare(String(b.CreatedAt || ''));
      }
      // Niveli renditet sipas peshës. Alfabetikisht "Shkelje e rëndë" bie mes
      // "Kujtesë" dhe "Vërejtje", pra kolona jepte një rend pa kuptim.
      if (orderBy === 'Severity') {
        return dir * ((SEVERITY_RANK[a.Severity] || 0) - (SEVERITY_RANK[b.Severity] || 0));
      }
      return dir * String(a[orderBy] ?? '').localeCompare(String(b[orderBy] ?? ''), 'sq');
    });
  }, [data, order, orderBy]);

  const paged = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  function handleSort(col) {
    setOrder(orderBy === col && order === 'asc' ? 'desc' : 'asc');
    setOrderBy(col);
  }

  // ── Stats ──────────────────────────────────────────────────────────────────
  const stats = {
    total:  data.length,
    kujt:   data.filter(r => r.Severity === 'Kujtesë').length,
    ver:    data.filter(r => r.Severity === 'Vërejtje').length,
    shkeljë:data.filter(r => r.Severity === 'Shkelje e rëndë').length,
  };

  // ── Department options ─────────────────────────────────────────────────────
  const deptOptions = useMemo(() => {
    const deptMap = Object.fromEntries(departments.map(d => [String(d.Id), d.Name]));
    return Array.from(new Set(
      employees.map(e => deptMap[String(e.Department)] || e.DepartmentName || '').filter(Boolean)
    )).sort();
  }, [employees, departments]);

  // ── Resend email ───────────────────────────────────────────────────────────
  const resendEmail = useCallback(async (r) => {
    if (!r.Email) {
      setSnack({ open: true, msg: 'Ky punëtor nuk ka adresë email të regjistruar.', sev: 'error' });
      return;
    }
    setSendingId(r.Id || r._localId);
    try {
      const pdfBase64  = getWarningPdfBase64(r);
      const pdfFilename = getWarningPdfFilename(r);
      const payload = {
        to: r.Email,
        subject: `Vërejtje: ${r.Subject || 'Vërejtje'}`,
        body: `Përshëndetje ${r.FirstName || ''} ${r.LastName || ''},\n\nJu është dhënë një vërejtje (${r.Severity || ''}).\n\nSubjekti: ${r.Subject || '—'}\n\n${r.Description || ''}\n\nFaleminderit,\nDepartamenti HR`,
        pdfBase64,
        pdfFilename,
      };
      const res = await apiService().execute('sendWarningEmail', payload, 'post');
      if (res?.success) {
        setSnack({ open: true, msg: `Email-i u dërgua me sukses te ${r.Email} (me PDF bashkëngjitur)!`, sev: 'success' });
      } else {
        // Serveri e thotë saktësisht pse dështoi (kredenciale, host, adresë) —
        // "Kontrolloni konfigurimin SMTP" e fshihte atë përgjigje.
        setSnack({ open: true, msg: res?.error || 'Email-i nuk u dërgua. Kontrolloni konfigurimin SMTP.', sev: 'error' });
      }
    } catch (e) {
      setSnack({ open: true, msg: e?.message || 'Gabim gjatë dërgimit të email-it.', sev: 'error' });
    } finally {
      setSendingId(null);
    }
  }, []);

  // ── Fshi vërejtjen ─────────────────────────────────────────────────────────
  // Një vërejtje jeton në tri vende: tabela e serverit, rreshtat lokalë që
  // s'janë ruajtur ende, dhe historiku i dërgesave. Fshirja vetëm te serveri e
  // linte rreshtin në ekran — ndaj hiqet nga të tria, pastaj nga gjendja.
  const deleteWarning = useCallback(async (r) => {
    const id   = r.Id ?? r._localId;
    const name = `${r.FirstName || ''} ${r.LastName || ''}`.trim();
    if (id == null) return;
    const svr = severityMeta(r.Severity);
    const okToGo = await confirm({
      title: 'Fshi vërejtjen',
      message: 'Vërejtja fshihet përgjithmonë. Ky veprim nuk kthehet.',
      // Tri fusha të ngjitura me "·" lexoheshin si një fjali e vetme e prishur:
      // pa etiketa, "Mungesa pa njoftim" mund të ishte emër, subjekt a shënim.
      // Të etiketuara, dialogu thotë saktësisht cilin rresht po prek.
      details: (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1.5, rowGap: 0.85, alignItems: 'center' }}>
          {[
            ['Punëtori', name || <em style={{ opacity: 0.7 }}>Pa punëtor të lidhur</em>],
            ['Subjekti', r.Subject || '—'],
            ['Niveli', (
              <Chip size="small" label={r.Severity || '—'}
                sx={{ fontWeight: 600, fontSize: '0.7rem', height: 20,
                      color: svr.color, bgcolor: svr.bg, border: '1px solid', borderColor: svr.border }} />
            )],
            ['Data', dateText(r.CreatedAt) || '—'],
          ].map(([label, value]) => (
            <React.Fragment key={label}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {label}
              </Typography>
              <Box sx={{ fontSize: '0.85rem', fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {value}
              </Box>
            </React.Fragment>
          ))}
        </Box>
      ),
      confirmLabel: 'Fshi',
    });
    if (!okToGo) return;

    setDeletingId(id);
    try {
      const onServer = rows.some(x => String(x.Id) === String(id));
      if (onServer) {
        const res = await apiService().delete('employeeWarnings', id);
        // Backend-i kthen `success: false` në vend të një përjashtimi kur rruga
        // e refuzon (p.sh. 403) — pa këtë kontroll rreshti zhdukej nga ekrani
        // ndërsa mbetej i gjallë në bazë.
        if (res && res.success === false) throw new Error(res.error || 'Fshirja u refuzua nga serveri.');
        setRows(prev => prev.filter(x => String(x.Id) !== String(id)));
      }
      try { warningsLocal().remove(id); }     catch (_) {}
      try { warningsLocal().removeSent(id); } catch (_) {}
      setLocalRows(prev => prev.filter(x => String(x.Id ?? x._localId) !== String(id)));
      setSentRows(prev  => prev.filter(x => String(x.Id ?? x._localId) !== String(id)));
      setPreviewRow(prev => (prev && String(prev.Id ?? prev._localId) === String(id) ? null : prev));
      setSnack({ open: true, msg: 'Vërejtja u fshi.', sev: 'success' });
    } catch (e) {
      setSnack({ open: true, msg: e?.message || 'Vërejtja nuk u fshi.', sev: 'error' });
    } finally {
      setDeletingId(null);
    }
  }, [rows, confirm]);

  // ── Exports ────────────────────────────────────────────────────────────────
  // Të katërta lexojnë të njëjtin përshkrim, ndaj kolonat dhe koka janë të
  // njëjta kudo. Filtrat shkruhen në kokë: një raport i filtruar që nuk e thotë
  // lexohet si i plotë, dhe numrat e tij nuk përputhen me askënd tjetër.
  const activeFilters = useMemo(() => {
    const out = [];
    if (search) out.push(`kërkim "${search}"`);
    if (emp !== 'all') {
      const e = employees.find(x => String(x.Id) === String(emp));
      out.push(`punëtori ${e ? `${e.FirstName} ${e.LastName}`.trim() : emp}`);
    }
    if (dept !== 'all')  out.push(`departamenti ${dept}`);
    if (level !== 'all') out.push(`niveli ${level}`);
    if (from) out.push(`prej ${dateText(from)}`);
    if (to)   out.push(`deri ${dateText(to)}`);
    return out;
  }, [search, emp, dept, level, from, to, employees]);

  const layout = useCallback(() => warningsLayout(sorted, {
    company,
    filters: activeFilters,
    generatedAt: new Date().toLocaleDateString('sq-AL'),
  }), [sorted, company, activeFilters]);

  const dlCSV   = () => downloadWarningsCsv(layout());
  const dlXLS   = () => exportWarningsExcel(layout());
  const dlPDF   = () => downloadWarningsPdf(layout());
  const doPrint = () => printWarnings(layout());

  /**
   * Veprimet e një rreshti — të njëjtat te tabela dhe te kartat e telefonit.
   *
   * Ishin të shkruara vetëm brenda qelizës së tabelës; pamja e telefonit do
   * t'i kishte kopjuar, dhe kopja e dytë do të mbetej pas së parës në çdo
   * ndryshim të mëpasshëm.
   */
  const RowActions = ({ r, dense = false }) => {
    const svr = severityMeta(r.Severity);
    const busyId = r.Id || r._localId;
    return (
      <Stack
        direction="row" spacing={0.25} justifyContent="flex-end" alignItems="center"
        className="row-actions"
        sx={{
          opacity: dense ? 1 : 0.62,
          transition: 'opacity .15s',
          // Pa hover nuk ka si të ndizen: në ekran me prekje rrinë të plota,
          // përndryshe veprimet do të dukeshin përgjithmonë të çaktivizuara.
          '@media (hover: none)': { opacity: 1 },
        }}
      >
        <Tooltip title="Parapamje" arrow>
          <IconButton size="small" onClick={() => setPreviewRow(r)}
            sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'grey.100', color: 'text.primary' } }}>
            <VisibilityIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Shkarko PDF individual" arrow>
          <IconButton size="small" onClick={() => toSingleWarningPdf(r)}
            sx={{ color: 'text.secondary', '&:hover': { bgcolor: svr.bg, color: svr.color } }}>
            <PictureAsPdfIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={r.Email ? `Dërgo përsëri te ${r.Email}` : 'Pa email'} arrow>
          <span>
            <IconButton size="small" disabled={!r.Email || sendingId === busyId} onClick={() => resendEmail(r)}
              sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'success.soft', color: 'success.main' }, '&.Mui-disabled': { color: 'action.disabled' } }}>
              {sendingId === busyId ? <CircularProgress size={14} color="success" /> : <SendIcon sx={{ fontSize: 15 }} />}
            </IconButton>
          </span>
        </Tooltip>
        {canDelete && (
          <>
            {/* Fshirja nuk kthehet — një ndarës e mban larg klikimit që synonte "Dërgo". */}
            <Box sx={{ width: '1px', height: 16, bgcolor: 'divider', mx: 0.4 }} />
            <Tooltip title="Fshi vërejtjen" arrow>
              <span>
                <IconButton size="small" disabled={deletingId === busyId} onClick={() => deleteWarning(r)}
                  sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'error.soft', color: 'error.main' }, '&.Mui-disabled': { color: 'action.disabled' } }}>
                  {deletingId === busyId ? <CircularProgress size={14} color="error" /> : <DeleteOutlineIcon sx={{ fontSize: 17 }} />}
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </Stack>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <ReportPage sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

      {/* ── Header card: stats + toolbar + filters ── */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>

        {/* Koka: titulli, shiriti i qetë i numrave, veprimet */}
        <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* "Vërejtjet" e shkruante PageHeader-i i faqes një rresht më lart —
              këtu mbetet vetëm numri, si zëri i parë i të njëjtit shirit. */}
          <Box sx={{ px: 1.5, py: 0.4, borderLeft: '3px solid', borderColor: 'text.disabled', borderRadius: '0 4px 4px 0' }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.1 }}>
              {loading ? '—' : stats.total}
            </Typography>
            <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>Gjithsej</Typography>
          </Box>

          {/* Numrat si shirit i qetë me buzë 3px, jo katër çipa me ngjyra të plota.
              Çipat konkurronin me çipat e nivelit brenda tabelës — e njëjta formë,
              e njëjta ngjyrë, dy kuptime. */}
          <Stack direction="row" spacing={0} sx={{ flexWrap: 'wrap' }}>
            {SEVERITY_LEVELS.map((lvl) => {
              const meta = severityMeta(lvl);
              const n = data.filter(r => r.Severity === lvl).length;
              const on = level === lvl;
              return (
                <Box
                  key={lvl}
                  role="button"
                  tabIndex={0}
                  onClick={() => setLevel(on ? 'all' : lvl)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setLevel(on ? 'all' : lvl); } }}
                  sx={{
                    cursor: 'pointer', px: 1.5, py: 0.4, ml: 1.5,
                    borderLeft: '3px solid', borderColor: meta.color,
                    bgcolor: on ? meta.bg : 'transparent',
                    borderRadius: '0 4px 4px 0',
                    transition: 'background-color 160ms',
                    '&:hover': { bgcolor: meta.soft },
                    '&:focus-visible': { outline: '2px solid', outlineColor: meta.color, outlineOffset: 2 },
                  }}
                >
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.1, color: meta.color }}>{n}</Typography>
                  <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>{lvl}</Typography>
                </Box>
              );
            })}
          </Stack>

          <Box sx={{ flex: 1 }} />

          <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
            {/* Eksportet si një copë e vetme: janë e njëjta gjë në katër formate,
                jo katër veprime të palidhura. */}
            <ExportMenu onCsv={dlCSV} onExcel={dlXLS} onPdf={dlPDF} onPrint={doPrint} disabled={!sorted.length} />
            <Button component={Link} to="/warnings/add" variant="contained" size="small" startIcon={<AddIcon />}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, fontSize: '0.78rem', py: 0.55 }}>
              Shto vërejtje
            </Button>
          </Stack>
        </Box>

        {/* Filter row */}
        <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small" placeholder="Kërko emër, subjekt ose përshkrim…"
            value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 14, color: 'text.disabled' }} /></InputAdornment> }}
            sx={{ width: 230, '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.55 }, '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: 'background.paper' } }}
          />
          <TextField select size="small" value={emp} onChange={e => setEmp(e.target.value)}
            SelectProps={{ renderValue: (v) => {
              if (v === 'all') return 'Punëtori';
              const e = employees.find(x => String(x.Id) === String(v));
              return e ? `${e.FirstName || ''} ${e.LastName || ''}`.trim() : 'Punëtori';
            } }}
            sx={{ width: 165, '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.6 }, '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: 'background.paper' } }}>
            <MenuItem value="all" sx={{ fontSize: '0.8rem' }}>Të gjithë</MenuItem>
            {employees.map(e => <MenuItem key={e.Id} value={String(e.Id)} sx={{ fontSize: '0.8rem' }}>{e.FirstName} {e.LastName}</MenuItem>)}
          </TextField>
          <TextField select size="small" value={dept} onChange={e => setDept(e.target.value)}
            SelectProps={{ renderValue: v => v === 'all' ? 'Departamenti' : v }}
            sx={{ width: 140, '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.6 }, '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: 'background.paper' } }}>
            <MenuItem value="all" sx={{ fontSize: '0.8rem' }}>Të gjitha</MenuItem>
            {deptOptions.map(d => <MenuItem key={d} value={d} sx={{ fontSize: '0.8rem' }}>{d}</MenuItem>)}
          </TextField>
          <TextField select size="small" value={level} onChange={e => setLevel(e.target.value)}
            SelectProps={{ renderValue: v => v === 'all' ? 'Niveli' : v }}
            sx={{ width: 130, '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.6 }, '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: 'background.paper' } }}>
            <MenuItem value="all" sx={{ fontSize: '0.8rem' }}>Të gjithë</MenuItem>
            {SEVERITY_LEVELS.map(l => <MenuItem key={l} value={l} sx={{ fontSize: '0.8rem' }}>{l}</MenuItem>)}
          </TextField>
          <TextField size="small" type="date" label="Prej" InputLabelProps={{ shrink: true }} value={from} onChange={e => setFrom(e.target.value)}
            sx={{ width: 140, '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.55 }, '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: 'background.paper' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }} />
          <TextField size="small" type="date" label="Deri" InputLabelProps={{ shrink: true }} value={to} onChange={e => setTo(e.target.value)}
            sx={{ width: 140, '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.55 }, '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: 'background.paper' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }} />
          {(search || emp !== 'all' || dept !== 'all' || level !== 'all' || from || to) && (
            <Chip size="small" label="Pastro" onDelete={() => { setSearch(''); setEmp('all'); setDept('all'); setLevel('all'); setFrom(''); setTo(''); }}
              sx={{ fontSize: '0.7rem', height: 22 }} />
          )}
          {activeFilters.length ? (
            <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto', whiteSpace: 'nowrap' }}>{data.length} rezultate</Typography>
          ) : null}
        </Box>
      </Paper>

      {/* ── Table card ── */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        {/* Te telefoni tabela tregonte vetëm emrin dhe veprimet; subjekti, niveli
            dhe data rrinin 392px pas buzës, të arritshme vetëm me rrëshqitje
            anash. Pra pikërisht ajo që i thotë çfarë është vërejtja. Poshtë,
            i njëjti rresht si kartë. */}
        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
          {loading && <Stack alignItems="center" sx={{ py: 5 }}><CircularProgress size={22} /></Stack>}
          {!loading && Boolean(loadError) && <Box sx={{ p: 2 }}><Alert severity="error">{loadError}</Alert></Box>}
          {!loading && !loadError && paged.length === 0 && (
            <Typography sx={{ py: 5, px: 2, textAlign: 'center', color: 'text.secondary', fontSize: '.85rem' }}>
              {activeFilters.length
                ? 'Asnjë vërejtje nuk i përgjigjet filtrave të zgjedhur.'
                : 'Ende nuk është shënuar asnjë vërejtje.'}
            </Typography>
          )}
          {!loading && !loadError && paged.map(r => {
            const svr = severityMeta(r.Severity);
            const name = `${r.FirstName} ${r.LastName}`.trim();
            const noName = !name;
            return (
              <Box key={r.Id || r._localId || `${r.Employee}-${r.CreatedAt}-${r.Subject}`}
                sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}>
                <Stack direction="row" alignItems="flex-start" spacing={1.25}>
                  <Avatar src={r.PhotoSrc || undefined} alt={name || undefined}
                    sx={{ width: 32, height: 32, fontSize: 12, fontWeight: 600, flexShrink: 0,
                          bgcolor: noName ? 'grey.100' : 'primary.soft', color: noName ? 'text.disabled' : 'primary.main' }}>
                    {noName ? '–' : (!r.PhotoSrc && initials(r.FirstName, r.LastName))}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" fontWeight={noName ? 400 : 700} noWrap
                      sx={noName ? { color: 'text.disabled', fontStyle: 'italic' } : undefined}>
                      {noName ? 'Pa punëtor të lidhur' : name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                      {[r.DepartmentName && r.DepartmentName !== '—' ? r.DepartmentName : null, dateText(r.CreatedAt) || '—']
                        .filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                  <Chip size="small" label={r.Severity || '—'} sx={{
                    flexShrink: 0, fontWeight: 600, fontSize: '0.68rem', height: 21,
                    color: svr.color, bgcolor: svr.bg, border: '1px solid', borderColor: svr.border,
                  }} />
                </Stack>
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>{r.Subject || '—'}</Typography>
                {r.Description && (
                  <Typography variant="caption" color="text.secondary"
                    sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', mt: 0.25 }}>
                    {r.Description}
                  </Typography>
                )}
                <Box sx={{ mt: 1 }}><RowActions r={r} dense /></Box>
              </Box>
            );
          })}
        </Box>

        <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)', overflow: 'auto', display: { xs: 'none', sm: 'block' } }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, minWidth: 150 }}>
                    <TableSortLabel active={orderBy === 'Name'} direction={orderBy === 'Name' ? order : 'asc'} onClick={() => handleSort('Name')}>
                      Punëtori
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>Subjekti</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 92 }}>
                    <TableSortLabel active={orderBy === 'Severity'} direction={orderBy === 'Severity' ? order : 'asc'} onClick={() => handleSort('Severity')}>
                      Niveli
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 180, display: { xs: 'none', lg: 'table-cell' } }}>Përshkrimi</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 88 }}>
                    <TableSortLabel active={orderBy === 'CreatedAt'} direction={orderBy === 'CreatedAt' ? order : 'asc'} onClick={() => handleSort('CreatedAt')}>
                      Data
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 100, display: { xs: 'none', lg: 'table-cell' } }}>Lëshuar nga</TableCell>
                  {/* Veprimet ngjiten djathtas: edhe kur tabela rrëshqet, butoni
                      që do klikuar mbetet në pamje. Më parë e tërë kolona —
                      parapamja, PDF-ja, dërgimi, fshirja — dilte jashtë ekranit
                      në një laptop të zakonshëm, dhe s'kishte si arrihej pa
                      rrëshqitur anash. */}
                  <TableCell align="right" sx={{
                    fontWeight: 700, minWidth: canDelete ? 132 : 104,
                    position: 'sticky', right: 0, zIndex: 3,
                    bgcolor: 'background.paper',
                    borderLeft: '1px solid', borderColor: 'divider',
                  }}>Veprimet</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Tri gjendje të ndara. Më parë të treja jepnin të njëjtën fjali:
                    "nuk u gjetën vërejtje" — edhe kur serveri kishte rënë, edhe
                    kur po ngarkohej ende, edhe kur baza ishte vërtet e zbrazët. */}
                {loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={22} />
                    </TableCell>
                  </TableRow>
                )}
                {/* `loadError` nis si '' — dhe `x && ''` jep string bosh, të cilin
                    React e fut si nyje teksti brenda <tbody>. Prandaj Boolean(). */}
                {!loading && Boolean(loadError) && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <Alert severity="error" sx={{ display: 'inline-flex', textAlign: 'left' }}>{loadError}</Alert>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && !loadError && paged.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                      {activeFilters.length
                        ? 'Asnjë vërejtje nuk i përgjigjet filtrave të zgjedhur.'
                        : 'Ende nuk është shënuar asnjë vërejtje.'}
                    </TableCell>
                  </TableRow>
                )}
                {paged.map(r => {
                  const svr    = severityMeta(r.Severity);
                  // 6 nga 7 rreshtat e seed-it s'kanë worker_id, ndaj emri del bosh.
                  // Një avatar me ngjyrën e ashpërsisë dhe një "?" e bënte rreshtin
                  // të dukej i prishur; pa punëtor ai rri neutral dhe i etiketuar.
                  const name   = `${r.FirstName} ${r.LastName}`.trim();
                  const noName = !name;
                  const dateStr= dateText(r.CreatedAt) || '—';
                  return (
                    <TableRow
                      key={r.Id || r._localId || `${r.Employee}-${r.CreatedAt}-${r.Subject}`}
                      hover
                      // Rreshti nuk ngjyroset i tëri. Ngjyra e ashpërsisë rri te
                      // çipi dhe te avatari; e shtrirë mbi tërë rreshtin ajo
                      // kthehej në sfond — dhe një tabelë ku çdo rresht ka ngjyrë
                      // është një tabelë ku asnjë ngjyrë nuk bie në sy.
                      sx={{
                        '&:hover': { bgcolor: 'grey.50' },
                        // Veprimet ndizen kur rreshti është nën kursor ose kur
                        // fokusi hyn me tastierë — pa këtë të dytën, navigimi me
                        // Tab do të klikonte butona gjysmë të tejdukshëm.
                        '&:hover .row-actions, & .row-actions:focus-within': { opacity: 1 },
                      }}
                    >
                      {/* Punëtori */}
                      <TableCell sx={{ py: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {/* Avatari identifikon personin, jo ashpërsinë. Me
                              ngjyrën e nivelit, i njëjti punëtor dilte gri në
                              një rresht dhe qelibar në tjetrin — sikur të ishin
                              dy veta. Niveli e ka çipin e vet një kolonë më tej. */}
                          <Avatar
                            src={r.PhotoSrc || undefined}
                            alt={name || undefined}
                            sx={{
                              width: 28, height: 28, fontSize: 11, fontWeight: 600,
                              // I njëjti avatar punëtori si te raporti i vlerësimeve:
                              // tinta e shtëpisë, jo një ngjyrë e re për këtë ekran.
                              bgcolor: noName ? 'grey.100' : 'primary.soft',
                              color:   noName ? 'text.disabled' : 'primary.main',
                            }}
                          >
                            {noName ? '–' : (!r.PhotoSrc && initials(r.FirstName, r.LastName))}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={noName ? 400 : 600} noWrap sx={noName ? { color: 'text.disabled', fontStyle: 'italic' } : undefined}>
                              {noName ? 'Pa punëtor të lidhur' : name}
                            </Typography>
                            {r.DepartmentName && r.DepartmentName !== '—' && (
                              <Typography variant="caption" color="text.secondary" noWrap>{r.DepartmentName}</Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      {/* Subjekti */}
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" noWrap title={r.Subject}>{r.Subject || '—'}</Typography>
                      </TableCell>
                      {/* Niveli */}
                      <TableCell sx={{ py: 1 }}>
                        <Chip
                          size="small"
                          label={r.Severity || '—'}
                          sx={{
                            fontWeight: 600, fontSize: '0.71rem', height: 22,
                            color: svr.color, bgcolor: svr.bg,
                            border: '1px solid', borderColor: svr.border,
                          }}
                        />
                      </TableCell>
                      {/* Përshkrimi */}
                      <TableCell sx={{ py: 1, maxWidth: 320, display: { xs: 'none', lg: 'table-cell' } }}>
                        <Tooltip title={r.Description} arrow placement="top-start">
                          <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300, color: 'text.secondary' }}>
                            {r.Description || '—'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      {/* Data */}
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" noWrap>{dateStr}</Typography>
                      </TableCell>
                      {/* Lëshuar nga */}
                      <TableCell sx={{ py: 1, display: { xs: 'none', lg: 'table-cell' } }}>
                        <Typography variant="body2" noWrap color="text.secondary">{r.IssuedBy || '—'}</Typography>
                      </TableCell>
                      {/* Veprimet */}
                      {/* Katër ikona me katër ngjyra, të shumëzuara me çdo rresht,
                          e kthenin kolonën në një bandë ngjyrash që s'thoshte gjë.
                          Në qetësi rrinë të gjitha gri; ngjyra vjen te hover-i i
                          butonit — atëherë kur ajo ka çfarë të dallojë. */}
                      <TableCell align="right" sx={{
                        py: 0.5,
                        position: 'sticky', right: 0, zIndex: 1,
                        bgcolor: 'background.paper',
                        borderLeft: '1px solid', borderColor: 'divider',
                        // Pa këtë, sfondi i ngjitur mbulonte theksimin e rreshtit
                        // nën kursor dhe kolona dukej e shkëputur nga rreshti.
                        'tr:hover &': { bgcolor: 'grey.50' },
                      }}>
                        <RowActions r={r} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ── Pagination ───────────────────────────────────────────────── */}
          <TablePagination
            component="div"
            count={sorted.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Rreshta:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} nga ${count}`}
            sx={{ borderTop: '1px solid', borderColor: 'divider' }}
          />
      </Card>

      {/* ── Preview Dialog ───────────────────────────────────────────────── */}
      {previewRow && (() => {
        const pr  = previewRow;
        const svc = severityMeta(pr.Severity);
        const nm  = `${pr.FirstName || ''} ${pr.LastName || ''}`.trim() || '—';
        const dt  = dateText(pr.CreatedAt) || '—';
        return (
          <Dialog open onClose={() => setPreviewRow(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
            <Box sx={{ height: 6, bgcolor: svc.color }} />
            <DialogTitle sx={{ pb: 1, pt: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar
                    src={pr.PhotoSrc || undefined}
                    alt={nm !== '—' ? nm : undefined}
                    sx={{ width: 40, height: 40, fontWeight: 600,
                          bgcolor: 'primary.soft', color: 'primary.main' }}
                  >
                    {!pr.PhotoSrc && initials(pr.FirstName, pr.LastName)}
                  </Avatar>
                  <Box>
                    <Typography fontWeight={700} variant="body1">{nm}</Typography>
                    {(pr.Position || pr.DepartmentName) && (
                      <Typography variant="caption" color="text.secondary">
                        {[pr.Position, pr.DepartmentName !== '—' ? pr.DepartmentName : ''].filter(Boolean).join(' · ')}
                      </Typography>
                    )}
                  </Box>
                </Stack>
                <IconButton size="small" onClick={() => setPreviewRow(null)}><CloseIcon fontSize="small" /></IconButton>
              </Stack>
            </DialogTitle>
            <DialogContent dividers sx={{ pt: 2 }}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={2}>
                  <Box flex={1}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>Subjekti</Typography>
                    <Typography variant="body1" fontWeight={600} mt={0.25}>{pr.Subject || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>Niveli</Typography>
                    <Box mt={0.25}>
                      <Chip size="small" label={pr.Severity || '—'}
                        sx={{ fontWeight: 700, color: svc.color, bgcolor: svc.bg,
                              border: '1px solid', borderColor: svc.border }} />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>Data</Typography>
                    <Typography variant="body2" mt={0.25}>{dt}</Typography>
                  </Box>
                </Stack>
                <Box>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>Përshkrimi</Typography>
                  <Paper elevation={0} sx={{ mt: 0.75, p: 1.5, bgcolor: svc.bg + '80', borderRadius: 2, border: '1px solid', borderColor: `${svc.color}30` }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{pr.Description || '—'}</Typography>
                  </Paper>
                </Box>
                {pr.IssuedBy && (
                  <Box>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>Lëshuar nga</Typography>
                    <Typography variant="body2" mt={0.25}>{pr.IssuedBy}</Typography>
                  </Box>
                )}
                {pr.Email && (
                  <Box>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>Email i punëtorit</Typography>
                    <Typography variant="body2" mt={0.25} color="text.secondary">{pr.Email}</Typography>
                  </Box>
                )}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
              <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon />}
                onClick={() => toSingleWarningPdf(pr)}
                sx={{ textTransform: 'none', borderRadius: 2 }}>
                Shkarko PDF
              </Button>
              <Button size="small" variant="contained" startIcon={<SendIcon />}
                disabled={!pr.Email || sendingId === (pr.Id || pr._localId)}
                onClick={() => { resendEmail(pr); setPreviewRow(null); }}
                sx={{ textTransform: 'none', borderRadius: 2 }}>
                Dërgo përsëri
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* Njoftimi i përbashkët — i njëjti kudo në aplikacion. */}
      <Toast
        open={snack.open}
        message={snack.msg}
        variant={snack.sev}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
      />

      {confirmUI}

    </ReportPage>
  );
}
